/*
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';

export interface AutojobsSettingConfirmationDialogProps {
  enabled?: boolean;
  onAction?: (result: boolean, enabled?: boolean) => void;
}

/**
 * Displays a confirmation dialog to the user asking about
 * enabling / disabling Account setting.
 * 
 * ```typescript
 * 
 * import AutojobsSettingConfirmationDialog from './AutojobsSettingConfirmationDialog';
 * 
 * <AutojobsSettingConfirmationDialog enabled={isEnabled} onAction={onAction} />
 * 
 * ```
 */
export const AutojobsSettingConfirmationDialog = ({
  enabled,
  onAction
}: AutojobsSettingConfirmationDialogProps) => {
  const {
    yesContinue: confirmationContinue,
    neverMind: confirmationNevermind
  } = translate('common:editors.shared', {returnObjects: true});

  const closeConfirmation = useCallback(function(result: boolean): () => void {
    return () => {
      onAction?.(result, !enabled);
    };
  }, [enabled]);

  return (
    <UserNotificationDialog dismissLabel={confirmationNevermind}
      actionLabel={confirmationContinue}
      title={translate('common:editors.autojobs.disableTitle')}
      message={translate('common:editors.autojobs.disableMessage')}
      onDismiss={closeConfirmation(false)}
      onAction={closeConfirmation(true)}
      allowWhitespace
      curvedCorners
      capitalize
      show/>
  );
};

export default memo(AutojobsSettingConfirmationDialog);
